import { MaybeRef, Ref, unref, ref } from 'vue'
import domtoimage from 'dom-to-image-more'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import { storeToRefs } from 'pinia'
import { until } from '@vueuse/core'
import { usePersonalStore } from '../stores/personal'
import { useThemeStore } from '../stores/theme'
import { useAnalyzeStore } from '../stores/analyze'
import {
  Age,
  AgencyName,
  Gender,
  MAPPED_COUNTRY_TO_X,
  MAPPED_X_PLACEMENT_TO_FRIENDLY_NAME,
  Placement,
  ageRanges,
  agencies,
  genders,
  PLACEMENTS,
  GENDER_OPTIONS,
  TEST_AD_ACCOUNTS,
} from './constants'
import { i18n, isJapanese } from './userLanguage'
import { api, ChartData, DeepThoughtPost, PRO_TARGETING_AUTH_TOKEN, schema } from '@/api'
import { messageService } from '@/app'
import { LabeledValueWithOptions } from '@/base'
import { useChromeExtension } from '@/extension'
import settings from '@/settings'
import jaCountries from '@/translations/countries_ja.json'
import { LabeledValue } from '@/utils'

export type FeatureFlag = 'Creators' | 'CampaignPlanner'

const ALLOWED_EMAILS_FEATURE_FLAG: Record<FeatureFlag, string[]> = {
  Creators: ['edolgow@x.com', 'tyler@x.com', 'jbierman@x.com'],
  CampaignPlanner: ['sbrewster@x.com', 'lnolan@x.com'],
}

export function featureFlag(flag: FeatureFlag) {
  const personalStore = usePersonalStore()
  const { signedInAs } = storeToRefs(personalStore)

  if (!signedInAs.value) return false
  const allowedEmails = ALLOWED_EMAILS_FEATURE_FLAG[flag] || []

  return allowedEmails.includes(signedInAs.value)
}

export function isSdb() {
  const personalStore = usePersonalStore()
  const { signedInAs } = storeToRefs(personalStore)

  return signedInAs.value?.includes('socialdatabase.com')
}

export function isTestAdAccount() {
  const { adAccountId } = useChromeExtension()
  return TEST_AD_ACCOUNTS.includes(adAccountId.value ?? '')
}

export function isAgency(agency: string | undefined): agency is AgencyName {
  return agencies.some((a) => a === agency)
}

export function getSubdomain() {
  const subdomainRegex = /(?:\/\/)(.*?)\.xprotargeting\.com/
  const subdomain = window.location.href.match(subdomainRegex)?.[1]

  if (subdomain !== 'www') {
    return subdomain
  }
}

export function mapCountryToXCountry(country?: string | undefined) {
  if (!country) return

  const strippedCountry = country.replace('Country — ', '')
  const mappedCountry = MAPPED_COUNTRY_TO_X[strippedCountry.toLowerCase()]
  if (mappedCountry && strippedCountry !== country) {
    return 'Country — ' + mappedCountry
  }

  return MAPPED_COUNTRY_TO_X[strippedCountry.toLowerCase()] || country
}

export function isPlacement(placement: string): placement is Placement {
  return PLACEMENTS.includes(placement as Placement)
}

export function friendlyPlacement(placement: string) {
  if (isPlacement(placement)) {
    return MAPPED_X_PLACEMENT_TO_FRIENDLY_NAME[placement]
  }

  return placement
}

export async function getLanguages() {
  const response = await api.call(schema.StandaloneLanguages.list, undefined, {
    headers: { Authorization: `Token ${PRO_TARGETING_AUTH_TOKEN}` },
  })
  return response
}

export async function getNetworkOperators() {
  const response = await api.call(schema.StandaloneNetworkOperators.list, undefined, {
    headers: { Authorization: `Token ${PRO_TARGETING_AUTH_TOKEN}` },
  })
  return response
}

export async function getInterests() {
  const response = await api.call(schema.StandaloneInterests.list, undefined, {
    headers: { Authorization: `Token ${PRO_TARGETING_AUTH_TOKEN}` },
  })
  return response
}

export async function getConversationTopics() {
  const response = await api.call(schema.StandaloneConversationTopics.list, undefined, {
    headers: { Authorization: `Token ${PRO_TARGETING_AUTH_TOKEN}` },
  })
  return response
}

export async function getMoviesTvShows() {
  const response = await api.call(schema.StandaloneMoviesTvShows.list, undefined, {
    headers: { Authorization: `Token ${PRO_TARGETING_AUTH_TOKEN}` },
  })
  return response
}

export async function searchConversationTopics(keywords: string) {
  const response = await api.call(
    schema.StandaloneConversationTopics.search,
    { body: { keywords } },
    { headers: { Authorization: `Token ${PRO_TARGETING_AUTH_TOKEN}` } },
  )
  return response.conversation_topics
}

export async function searchMoviesTvShows(keywords: string) {
  const response = await api.call(
    schema.StandaloneMoviesTvShows.search,
    { body: { keywords } },
    { headers: { Authorization: `Token ${PRO_TARGETING_AUTH_TOKEN}` } },
  )
  return response.movies_tv_shows
}

export async function searchInterests(keywords: string) {
  const response = await api.call(
    schema.StandaloneInterests.search,
    { body: { keywords } },
    { headers: { Authorization: `Token ${PRO_TARGETING_AUTH_TOKEN}` } },
  )
  return response.interests
}

export async function searchNetworkOperators(keywords: string) {
  const response = await api.call(
    schema.StandaloneNetworkOperators.search,
    { body: { keywords } },
    { headers: { Authorization: `Token ${PRO_TARGETING_AUTH_TOKEN}` } },
  )
  return response.network_operators
}

export function isGender(gender: string | null | undefined): gender is Gender {
  return genders.includes(gender as Gender)
}

export function isAge(age: string | null | undefined): age is Age {
  return ageRanges.includes(age as Age)
}

export function isNumber(n: any): n is number {
  return !isNaN(parseFloat(n)) && isFinite(n)
}

export function mapGender(gender: string | number | null | undefined): Gender {
  if (typeof gender === 'number' || isNumber(gender)) {
    gender = parseInt(gender.toString())
    if (gender === 1) {
      return 'MALE'
    }

    if (gender === 2) {
      return 'FEMALE'
    }

    return 'ANY'
  }

  if (!isGender(gender)) return 'ANY'

  const g = GENDER_OPTIONS.value.find(
    (g) => g.value.toLowerCase() === gender.toLowerCase() || g.label.toLowerCase() === gender.toLowerCase(),
  )?.value

  if (!g) return 'ANY'

  return g
}

export function removeEmptyValues(obj: MaybeRef<Record<any, any>>): Record<any, any> {
  const newObj = unref(obj)
  Object.keys(newObj).forEach((key) => {
    if (newObj[key] === undefined || newObj[key] === null || newObj[key] === '') {
      delete newObj[key]
    }
  })

  return newObj
}

export async function copyToClipboard(str: string | string[], success?: string) {
  if (!navigator.clipboard) return messageService.error('Failed to copy content. Please try a different browser.')
  if (typeof str !== 'string') str = str.join(', ')
  if (str.length === 0) return messageService.error('Nothing selected to copy.')

  await navigator.clipboard.writeText(str)
  messageService.success(success ?? 'Copied!')
}

export function zip<T = any>(a: T[], b: T[]): T[] {
  const zipped = Array.from(Array(Math.max(b.length, a.length)), (_, i) => [a[i], b[i]])
  const zippedFlat = zipped.flat().filter((el) => el !== undefined)

  return zippedFlat
}

export type LabeledRegion = LabeledValueWithOptions & { single: boolean; countryCodes: string[] }
let regions: LabeledRegion[] | undefined
const regionsLoading = ref(false)
export async function getRegions(single?: boolean, withGlobal?: boolean) {
  if (regions) {
    if (withGlobal) {
      return [
        { label: 'Global', value: 'Global', countryCodes: [], single: false, searchableAlternatives: ['all', 'any'] },
        ...regions.filter((r) => !single || r.single),
      ]
    }

    return regions.filter((r) => !single || r.single)
  }

  if (regionsLoading.value) {
    await until(regionsLoading).toBe(false)
    return getRegions(single, withGlobal)
  }

  try {
    regionsLoading.value = true

    const response = await api.call(schema.StandaloneAudienceRegion.list, undefined, {
      headers: { Authorization: `Token ${PRO_TARGETING_AUTH_TOKEN}` },
    })
    regions = response.map((r) => ({
      label: r.name,
      value: r.name,
      single: r.is_single_country,
      countryCodes: r.country_codes,
    }))
    const northKorea = regions.findIndex((r) => r.label === "Democratic People's Republic of Korea")
    if (northKorea !== -1) {
      regions[northKorea] = {
        ...regions[northKorea],
        info: 'This country is not available for targeting.',
        selectable: false,
      }
    }
    regions = similarNamedRegions(regions)
    regions = localizedRegions(regions)

    return getRegions(single, withGlobal)
  } finally {
    regionsLoading.value = false
  }
}

function localizedRegions(r: NonNullable<typeof regions>): NonNullable<typeof regions> {
  if (isJapanese()) {
    return r.map((region) => {
      let label = region.label.toLowerCase().replace(/ /g, '_')
      // @ts-expect-error ignores missing keys
      label = jaCountries[label] || region.label
      region.label = label

      return region
    })
  }

  return r
}

function similarNamedRegions(r: NonNullable<typeof regions>) {
  const alternativeNames = {
    netherlands: ['holland'],
    'united kingdom': ['gb', 'great britain', 'uk', 'england', 'scotland', 'wales', 'northern ireland'],
    'united states': ['united states of america', 'usa'],
    czechia: ['czech republic'],
    'democratic republic of the congo': [
      'Congo (The Democratic Republic Of The)',
      'Congo, The Democratic Republic Of The',
    ],
    'united arab emirates': ['uae', 'dubai', 'abu dhabi'],
    venezuela: ['venezuela, bolivarian republic of'],
    "democratic people's republic of korea": ['north korea'],
  }
  const transformedCountryFromX = Object.keys(MAPPED_COUNTRY_TO_X).reduce(
    (acc, key) => {
      acc[key] = [MAPPED_COUNTRY_TO_X[key].toLowerCase()]
      return acc
    },
    {} as Record<string, string[]>,
  )
  const similarNames = Object.assign({}, transformedCountryFromX, alternativeNames)

  return r.map((region) => {
    if (similarNames[region.label.toLowerCase()]) {
      region.searchableAlternatives = similarNames[region.label.toLowerCase()]
    }

    return region
  })
}

export function normalizeRegion<T = string | Pick<LabeledRegion, 'label' | 'value'>>(region: T): T {
  if (!regions) return region

  if (typeof region === 'string') {
    if (region.toLowerCase() === 'global') {
      return 'Global' as T
    }

    const isCountryCode = region.length === 2 || region.length === 3 // for USA
    const foundRegion = regions.find((r) =>
      `${isCountryCode ? r.countryCodes.join(' ') : `${r.value}|${r.label}|${r.searchableAlternatives?.join(' ') ?? ''}`}`
        .toLowerCase()
        .includes(region.toLowerCase()),
    )
    if (foundRegion) {
      return foundRegion.label as T
    }
  }

  if (region && typeof region === 'object' && 'label' in region && typeof region.label === 'string') {
    if (region.label.toLowerCase() === 'global') {
      return { ...region, label: 'Global', value: 'Global' } as T
    }

    const foundRegion = regions.find((r) =>
      `${r.value}|${r.label}|${r.searchableAlternatives?.join(' ') ?? ''}`
        .toLowerCase()
        .includes((region.label as any).toLowerCase()),
    )
    if (foundRegion) {
      return { ...region, ...foundRegion }
    }
  }

  return region
}

export function isDigitsWithDecimal(value: string) {
  return /^\d+([\.,]\d+)?$/.test(value)
}

export function isNullOrUndefined(value: any): value is null | undefined {
  return value === null || value === undefined
}

export function toNonNullable<T = any>(value: (T | undefined)[]): NonNullable<T>[] {
  const array = value.filter((v) => !isNullOrUndefined(v)) as NonNullable<T>[]

  return array
}

export function toUnique<T = any>(value: (T | undefined)[]): T[] {
  const array = value.filter((v) => !isNullOrUndefined(v)) as T[]
  const uniqueArray = Array.from(new Set(array))
  return uniqueArray
}

export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>

export function numberOfDaysInclusive(d0: Date, d1: Date) {
  return 1 + Math.round((d1.getTime() - d0.getTime()) / (24 * 3600 * 1000))
}

export function numberOfWeekends(d0: Date, d1: Date) {
  const days = numberOfDaysInclusive(d0, d1)
  const sundays = Math.floor((days + ((d0.getDay() + 6) % 7)) / 7)
  const currentDayAdjustment = (d1.getDay() === 6 ? 1 : 0) - (d0.getDay() === 0 ? 1 : 0)
  return 2 * sundays + currentDayAdjustment
}

export function numberOfWeekdays(d0: Date, d1: Date) {
  return numberOfDaysInclusive(d0, d1) - numberOfWeekends(d0, d1)
}

export function emailValidator(v: any, errorField?: Ref<string | undefined>, skipAccess?: boolean) {
  const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
  if (!v?.match(emailRegex)) return 'Please enter a valid email address'

  if (!skipAccess) {
    validateEmailAccess(v, errorField)
  }
  return undefined
}

export async function validateEmailAccess(v: string, errorField?: Ref<string | undefined>) {
  try {
    await api.call(
      schema.StandaloneAudienceRequest.validate_email,
      { body: { email: v } },
      { headers: { Authorization: `Token ${PRO_TARGETING_AUTH_TOKEN}` } },
    )
  } catch (e) {
    if (
      e &&
      typeof e === 'object' &&
      'message' in e &&
      typeof e.message === 'string' &&
      e.message.includes('Email: Invalid access') &&
      errorField &&
      typeof errorField.value === 'string'
    ) {
      errorField.value =
        'Only your X rep can request new audiences. Please contact your rep to request a new customized audience.'
    }
  }
}

export function getCountryCode(country: string) {
  country = normalizeRegion(country)
  const countryCode = regions?.find((r) => r.label.toLowerCase() === country.toLowerCase())?.countryCodes?.[0]

  return countryCode
}

export function getCountryName(countryCode: string) {
  countryCode = countryCode.toLowerCase()
  const countryName = regions?.find((r) => r.countryCodes?.[0]?.toLowerCase() === countryCode)?.label

  return countryName
}

function isDataset(el: HTMLElement & { dataset: Record<string, string> }) {
  return 'dataset' in el && typeof el.dataset === 'object'
}

// adjustClonedNode: (node) => {
//   if (node?.style?.border) {
//     node.style.border = 'none'
//   }
//   return node
// },
// filter: (node) => {
//   if (node?.classList?.contains('dodo-icon')) {
//     return false
//   }
//   return true
// },
export async function downloadElement(el: HTMLElement, name: string, opts?: Record<string, any>) {
  const analyzeStore = useAnalyzeStore()
  const { downloadBackground } = storeToRefs(analyzeStore)
  const themeStore = useThemeStore()
  const { black } = storeToRefs(themeStore)
  const bgcolor = downloadBackground.value === 'transparent' ? 'transparent' : black.value ? '#27272a' : 'white'

  const initialScrollbarWidth = document.documentElement.style.scrollbarWidth
  try {
    document.documentElement.style.setProperty('--main-scollbar-width', 'none')
    const response = await domtoimage.toPng(el, {
      copyDefaultStyles: false,
      bgcolor,
      ...opts,
      corsImg: {
        method: 'GET',
        url: settings.API_ROOT + schema.StandaloneImageDownloader.download_image_unique_name.path + '?url=#{cors}',
      },
    })

    const link = document.createElement('a')
    link.download = `${name}.png`
    link.href = response
    link.click()
  } finally {
    document.documentElement.style.setProperty('--main-scollbar-width', initialScrollbarWidth)
  }
}

export async function downloadZippedElements(
  els: Element[],
  names: string[],
  zipName: string,
  opts?: Record<string, any>,
) {
  const zip = new JSZip()
  const initialScrollbarWidth = document.documentElement.style.scrollbarWidth

  const analyzeStore = useAnalyzeStore()
  const { downloadBackground } = storeToRefs(analyzeStore)
  const themeStore = useThemeStore()
  const { black } = storeToRefs(themeStore)

  try {
    document.documentElement.style.setProperty('--main-scollbar-width', 'none')
    const bgcolor = downloadBackground.value === 'transparent' ? 'transparent' : black.value ? '#27272a' : 'white'
    for (const [index, el] of els.entries()) {
      const otherOptions = isDataset(el as any) ? JSON.parse((el as any).dataset.downloadoptions ?? '{}') : {}
      const response = await domtoimage.toPng(el, {
        copyDefaultStyles: false,
        bgcolor,
        ...opts,
        ...otherOptions,
        corsImg: {
          method: 'GET',
          url: settings.API_ROOT + schema.StandaloneImageDownloader.download_image_unique_name.path + '?url=#{cors}',
        },
      })
      const base64Data = response.split(',')[1]
      zip.file(`${names[index]}.png`, base64Data, { base64: true })
    }

    const f = await zip.generateAsync({ type: 'blob' })

    saveAs(f, `${zipName}.zip`)
  } finally {
    document.documentElement.style.setProperty('--main-scollbar-width', initialScrollbarWidth)
  }
}

export function languageName(code: string) {
  const languageNames = new Intl.DisplayNames(['en'], {
    type: 'language',
  })

  try {
    return languageNames.of(code) || code
  } catch (e) {
    return code
  }
}

export async function validateHandle(handle: string) {
  const response = await api.call(
    schema.StandaloneAudienceRequest.validate_handle,
    { body: { handle } },
    { headers: { Authorization: `Token ${PRO_TARGETING_AUTH_TOKEN}` } },
  )
  return response
}

export function isKeywordList(query: any): query is { keywords: string[][] } {
  return typeof query === 'object' && Array.isArray(query.keywords) && query.keywords.every(Array.isArray)
}

export function findByLabel(obj: ChartData[], label: string): ChartData | undefined {
  return obj.find((item) => item.label.toLowerCase() === label.toLowerCase())
}

export function orderedGenders<T = ChartData[] | undefined>(genders: T): T {
  if (isNullOrUndefined(genders)) return genders

  return toNonNullable([
    findByLabel(genders as any, 'Female'),
    findByLabel(genders as any, 'Male'),
    ...(genders as ChartData[]).filter((g) => !['Female', 'Male'].includes(g.label)),
  ]) as T
}

export function tweetUrl(post: DeepThoughtPost) {
  return `https://x.com/${post.account.handle}/status/${post.tweet.id}`
}

export function ageRangeLabel<T extends string | LabeledValue<number>>(ageRange: T): T {
  const { t } = i18n.global

  if (typeof ageRange === 'object') {
    return { ...ageRange, label: ageRangeLabel(ageRange.label) }
  }

  if (typeof ageRange === 'string') {
    const range = ageRange.match(/(\d+)\s*To\s*(\d+)/)
    if (range) {
      const [from, to] = [parseInt(range[1], 10), parseInt(range[2], 10)]
      return t('age_from_to', { from, to }).replace('Age ', '') as T
    }

    const above = ageRange.match(/Age Over (\d+)/)
    if (above) {
      const age = parseInt(above[1], 10)
      return t('age_over', { age }).replace('Age ', '') as T
    }

    return ageRange
  }

  return ageRange
}

export function genderLabel<T extends string | LabeledValue<number>>(gender: T): T {
  const { t } = i18n.global

  if (typeof gender === 'object') {
    return { ...gender, label: genderLabel(gender.label) }
  }

  if (typeof gender === 'string') {
    if (gender.toLowerCase() === 'male') {
      return t('male') as T
    }

    if (gender.toLowerCase() === 'female') {
      return t('female') as T
    }
  }

  return gender
}
